/**
 * WebAuthn & Device Biometrics helpers for KredaFi
 * Compatible with Touch ID, Face ID, Android Biometrics, and Windows Hello.
 */

export function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]);
  }
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function b64urlToBuf(b64url: string): ArrayBuffer {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
  const bin = atob(b64 + pad);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    arr[i] = bin.charCodeAt(i);
  }
  return arr.buffer;
}

/**
 * Check if the device has a platform authenticator available
 * (Touch ID / Face ID / Android fingerprint / Windows Hello / Telegram Biometrics).
 */
export async function isBiometricsAvailable(): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  // 1. Check Telegram BiometricManager if running inside Telegram Mini App
  try {
    const tgBio = (window as any).Telegram?.WebApp?.BiometricManager;
    if (tgBio) {
      if (!tgBio.isInited) {
        await new Promise<void>((resolve) => {
          try {
            tgBio.init(() => resolve());
            setTimeout(resolve, 600);
          } catch {
            resolve();
          }
        });
      }
      if (tgBio.isBiometricAvailable) {
        return true;
      }
    }
  } catch (e) {
    console.warn('Telegram biometric check failed:', e);
  }

  // 2. Standard WebAuthn Platform Authenticator
  try {
    const PKC = (window as any).PublicKeyCredential;
    if (PKC && typeof PKC.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
      const available = await PKC.isUserVerifyingPlatformAuthenticatorAvailable();
      return Boolean(available);
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Register device biometric credential with userVerification: 'required'.
 * Stores credential ID in localStorage so unlock screen can target it.
 */
export async function registerBiometrics(rpName: string = 'KredaFi'): Promise<{ ok: boolean; error?: string }> {
  if (typeof window === 'undefined') return { ok: false, error: 'No window' };

  try {
    // 1. Try Telegram BiometricManager if available
    const tgBio = (window as any).Telegram?.WebApp?.BiometricManager;
    if (tgBio && tgBio.isInited && tgBio.isBiometricAvailable) {
      if (!tgBio.isAccessGranted) {
        const granted = await new Promise<boolean>((resolve) => {
          try {
            tgBio.requestAccess({ reason: 'Protect your wallet with biometrics' }, (res: boolean) => resolve(res));
          } catch {
            resolve(false);
          }
        });
        if (!granted) {
          return { ok: false, error: 'access_denied' };
        }
      }
      localStorage.setItem('security_biometrics', 'true');
      localStorage.setItem('security_bio_provider', 'telegram');
      triggerSecurityHaptic('success');
      return { ok: true };
    }

    // 2. WebAuthn Platform Authenticator
    const available = await isBiometricsAvailable();
    if (!available) {
      return { ok: false, error: 'not_available' };
    }

    const challenge = new Uint8Array(32);
    window.crypto.getRandomValues(challenge);

    const userID = Uint8Array.from(
      'kredafi_' + Math.random().toString(36).substring(2),
      (c) => c.charCodeAt(0)
    );

    const credential = (await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: {
          name: rpName,
          id: window.location.hostname,
        },
        user: {
          id: userID,
          name: 'kredafi_user',
          displayName: 'KredaFi Wallet',
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' }, // ES256
          { alg: -257, type: 'public-key' }, // RS256
        ],
        timeout: 60000,
        attestation: 'none',
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
          residentKey: 'discouraged',
        },
      },
    })) as PublicKeyCredential | null;

    if (credential) {
      const b64Id = bufToB64url(credential.rawId);
      localStorage.setItem('security_bio_cred_id', b64Id);
      localStorage.setItem('security_biometrics', 'true');
      localStorage.setItem('security_bio_provider', 'webauthn');
      triggerSecurityHaptic('success');
      return { ok: true };
    }
    return { ok: false, error: 'no_credential' };
  } catch (err: any) {
    console.error('Biometric registration failed:', err);
    return { ok: false, error: err?.name || err?.message || 'failed' };
  }
}

/**
 * Verify biometric credential using navigator.credentials.get or Telegram Biometrics.
 */
export async function verifyBiometrics(): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  const provider = localStorage.getItem('security_bio_provider');
  const tgBio = (window as any).Telegram?.WebApp?.BiometricManager;

  // 1. Check Telegram Biometrics
  if (tgBio && tgBio.isInited && tgBio.isBiometricAvailable && (provider === 'telegram' || tgBio.isAccessGranted)) {
    try {
      const ok = await new Promise<boolean>((resolve) => {
        try {
          tgBio.authenticate({ reason: 'Unlock wallet' }, (success: boolean) => {
            resolve(Boolean(success));
          });
        } catch {
          resolve(false);
        }
      });
      if (ok) {
        triggerSecurityHaptic('success');
        return true;
      }
      return false;
    } catch (e) {
      console.warn('Telegram biometric auth failed:', e);
    }
  }

  // 2. Standard WebAuthn
  const credIdB64 = localStorage.getItem('security_bio_cred_id');
  if (!credIdB64 || !window.PublicKeyCredential) {
    return false;
  }

  try {
    const challenge = new Uint8Array(32);
    window.crypto.getRandomValues(challenge);

    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        timeout: 60000,
        userVerification: 'required',
        rpId: window.location.hostname,
        allowCredentials: [{ id: b64urlToBuf(credIdB64), type: 'public-key' }],
      },
    });

    if (assertion) {
      triggerSecurityHaptic('success');
      return true;
    }
    return false;
  } catch (err) {
    console.warn('Biometric verification cancelled or failed:', err);
    return false;
  }
}

/**
 * Disable biometrics and clean up stored credential ID.
 */
export function disableBiometrics(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('security_biometrics');
  localStorage.removeItem('security_bio_cred_id');
  localStorage.removeItem('security_bio_provider');
  triggerSecurityHaptic('light');
}

/**
 * Security Event Constants and Helpers for cross-component sync
 */
export const SECURITY_EVENTS = {
  CHANGED: 'wt_security_changed',
  LOCK_NOW: 'wt_security_lock_now',
};

export function notifySecurityChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SECURITY_EVENTS.CHANGED));
  }
}

export function requestAppLock(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SECURITY_EVENTS.LOCK_NOW));
  }
}

/**
 * Get auto-lock timeout duration in milliseconds based on user preference.
 */
export function getAutoLockTimeoutMs(setting: string | null): number {
  switch (setting) {
    case 'immediately':
      return 0;
    case '1min':
      return 60 * 1000;
    case '5min':
      return 5 * 60 * 1000;
    case '15min':
      return 15 * 60 * 1000;
    default:
      return 0; // Default to 'immediately' (Bank standard)
  }
}

/**
 * Haptic feedback helper across Telegram Mini App and standard Web vibration.
 */
export function triggerSecurityHaptic(type: 'light' | 'medium' | 'error' | 'success' = 'light'): void {
  if (typeof window === 'undefined') return;

  // 1. Telegram WebApp HapticFeedback (if inside Telegram Mini App)
  if ((window as any).Telegram?.WebApp?.HapticFeedback) {
    try {
      const haptic = (window as any).Telegram.WebApp.HapticFeedback;
      if (type === 'error' || type === 'success') {
        haptic.notificationOccurred(type);
      } else {
        haptic.impactOccurred(type === 'medium' ? 'medium' : 'light');
      }
      return;
    } catch {
      // Fallback to web vibration
    }
  }

  // 2. Standard Web Navigator Vibration
  if (window.navigator && typeof window.navigator.vibrate === 'function') {
    try {
      if (type === 'error') {
        window.navigator.vibrate([80, 50, 80]);
      } else if (type === 'success') {
        window.navigator.vibrate([40, 30, 40]);
      } else {
        window.navigator.vibrate(20);
      }
    } catch {
      // Ignore
    }
  }
}
